/**
 * Route table — every route from BUILD_PLAN §13.
 *
 * All page modules use default exports. AgentList/AgentEditor are owned by
 * Builder G; TestConsole/CallLogs/CallDetail/Numbers by Builder H — the files
 * currently in src/pages are placeholders they replace wholesale.
 */

import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { Layout } from "./components/Layout";
import { Spinner } from "./components/Spinner";
import { RedirectIfAuthed, RequireAuth } from "./lib/auth";
import AgentEditor from "./pages/AgentEditor";
import AgentList from "./pages/AgentList";
import CallDetail from "./pages/CallDetail";
import CallLogs from "./pages/CallLogs";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Numbers from "./pages/Numbers";
import Register from "./pages/Register";
import Settings from "./pages/Settings";

// The test console pulls in the Pipecat JS SDK (which drags in daily-js via
// the transport) — lazy-load it so the SDK stays out of the main chunk.
const TestConsole = lazy(() => import("./pages/TestConsole"));

export default function App() {
  return (
    <Routes>
      {/* Public — split layout, bounce authed users back into the app. */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <Login />
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfAuthed>
            <Register />
          </RedirectIfAuthed>
        }
      />

      {/* Authenticated shell — left rail + topbar. */}
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="/agents" element={<AgentList />} />
        <Route path="/agents/new" element={<AgentEditor />} />
        <Route path="/agents/:id" element={<AgentEditor />} />
        <Route
          path="/agents/:id/test"
          element={
            <Suspense
              fallback={
                <div className="flex justify-center py-24">
                  <Spinner size="lg" />
                </div>
              }
            >
              <TestConsole />
            </Suspense>
          }
        />
        <Route path="/agents/:id/calls" element={<CallLogs />} />
        <Route path="/calls" element={<CallLogs />} />
        <Route path="/calls/:id" element={<CallDetail />} />
        <Route path="/numbers" element={<Numbers />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
